<template>
  <a-modal
    :visible="visible"
    title="Edit Progress Log"
    @ok="handleSubmit"
    @cancel="handleCancel"
    :confirm-loading="submitting"
    destroy-on-close
  >
    <a-spin :spinning="loading">
      <a-form :form="form">
        <a-form-item label="Teacher" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
          <a-select
            show-search
            option-filter-prop="children"
            v-decorator="[
              'teacher_id',
              { rules: [{ required: true, message: 'Please select a teacher' }] }
            ]"
            placeholder="Select a teacher"
            :disabled="!isAdmin && lessonPlan.tracking_mode !== 'open'"
          >
            <a-select-option
              v-for="staff in staffOptions"
              :key="staff.id"
              :value="staff.id"
            >
              {{ staff.first_name }} {{ staff.last_name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="Section" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
          <a-select
            v-decorator="[
              'section_id',
              { rules: [{ required: true, message: 'Please select a section' }] }
            ]"
            placeholder="Select a section"
            :disabled="!lessonPlan"
          >
            <a-select-option
              v-for="section in sections"
              :key="section.id"
              :value="section.id"
            >
              {{ section.name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <!-- Period -->
        <a-form-item label="Period" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
          <a-select
            v-decorator="[
              'period',
              { rules: [{ required: true, message: 'Please select a period' }] }
            ]"
            placeholder="Select a period"
          >
            <a-select-option :value="1">1st Period</a-select-option>
            <a-select-option :value="2">2nd Period</a-select-option>
            <a-select-option :value="3">3rd Period</a-select-option>
            <a-select-option :value="4">4th Period</a-select-option>
            <a-select-option :value="5">5th Period</a-select-option>
            <a-select-option :value="6">6th Period</a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="Status" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
          <a-select
            v-decorator="[
              'status',
              { rules: [{ required: true, message: 'Please select a status' }] }
            ]"
          >
            <a-select-option value="Not Started">Not Started</a-select-option>
            <a-select-option value="Started">Started</a-select-option>
            <a-select-option value="In Progress">In Progress</a-select-option>
            <a-select-option value="Completed">Completed</a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="Details" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
          <a-textarea
            rows="3"
            v-decorator="['details']"
            placeholder="What was covered?"
          />
        </a-form-item>

        <a-form-item label="Remarks" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
          <a-textarea
            rows="2"
            v-decorator="['remarks']"
            placeholder="Any extra notes?"
          />
        </a-form-item>

        <!-- Date -->
        <a-form-item
          label="Date"
          :label-col="{ span: 6 }"
          :wrapper-col="{ span: 16 }"
        >
          <a-date-picker v-decorator="['date']" style="width: 100%" />
        </a-form-item>

        <!-- Substitute Checkbox -->
        <a-form-item label="Substitute" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
          <a-checkbox v-decorator="['is_substitute', { valuePropName: 'checked', initialValue: false }]">
            I am taking this class as a substitute
          </a-checkbox>
        </a-form-item>
      </a-form>
    </a-spin>
  </a-modal>
</template>

<script>
import moment from 'moment'

export default {
  name: 'EditProgressModal',
  props: {
    visible: Boolean,
    progressLog: { type: Object, default: () => ({}) }, // The log to edit
    lessonPlan: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      form: this.$form.createForm(this),
      submitting: false,
      loading: false,
      sections: [],
      staffOptions: [],
      isAdmin: false,
    }
  },
  watch: {
    visible(val) {
      if (val) {
        const user = this.$store.getters.user
        this.isAdmin = user && ['superadmin', 'admin'].includes(user.role)

        this.loading = true
        if (this.lessonPlan && this.lessonPlan.subject_id) {
          this.getStaff()
        }
        if (this.lessonPlan && this.lessonPlan.class_id) {
          this.getSection().then(() => {
            if (this.progressLog) {
              // eslint-disable-next-line camelcase
              const { section_id, period, teacher_id, status, details, remarks, date, is_substitute: isSubstitute } = this.progressLog
              this.$nextTick(() => {
                this.form.setFieldsValue({
                  section_id,
                  period,
                  teacher_id,
                  status,
                  details,
                  remarks,
                  // eslint-disable-next-line camelcase
                  is_substitute: !!isSubstitute,
                  // eslint-disable-next-line camelcase
                  date: date ? moment(date) : moment(),
                })
              })
            }
          })
        }
      } else {
        this.form.resetFields()
        this.sections = []
        this.staffOptions = []
      }
    },
  },
  methods: {
    getStaff() {
      // Fetch teachers for this subject
      this.$http
        .get('/api/lesson-plan/teacher/subject-teachers', { params: { subject_id: this.lessonPlan.subject_id } })
        .then(({ data }) => {
          this.staffOptions = data.data
        })
        .catch(err => {
          this.$notification.error({ message: 'Failed to load staff list' })
          console.error(err)
        })
    },
    getSection() {
      this.loading = true
      return this.$http.get('/api/lesson-plan/teacher/sections-for-class', { params: { class_id: this.lessonPlan.class_id } })
        .then(({ data }) => {
          this.sections = data.data
        })
        .catch(() => {
          this.$notification.error({ message: 'Failed to load sections' })
        })
        .finally(() => {
          this.loading = false
        })
    },

    handleSubmit() {
      this.form.validateFields((err, values) => {
        if (!err) {
          const formattedValues = {
            ...values,
            date: values.date
              ? values.date.format('YYYY-MM-DD')
              : null,
          }

          this.submitting = true
          this.$http
            .put(`/api/lesson-plan/teacher/progress/${this.progressLog.id}`, formattedValues)
            .then(() => {
              this.$message.success('Progress log updated successfully!')
              this.$emit('updated')
              this.handleCancel()
            })
            .catch(() => {
              this.$notification.error({ message: 'Failed to update progress log' })
            })
            .finally(() => {
              this.submitting = false
            })
        }
      })
    },
    handleCancel() {
      this.form.resetFields()
      this.$emit('close')
    },
  },
}
</script>
